<template>
  <div class="card ml-15 mr-15">
    <div class="card-header">
      <nav aria-label="breadcrumb">
        <ol class="breadcrumb">
          <li class="breadcrumb-item">
            <a href="javascript:void(0)">LXD</a>
          </li>
          <li class="breadcrumb-item">
            <a :href="$router.resolve({name: 'lxd'}).href" @click.prevent="$router.push({name: 'lxd'})">List Instances</a>
          </li>
          <li aria-current="page" class="breadcrumb-item active">
            <a href="javascript:void(0)">{{ $route.params.id }}</a>
          </li>
        </ol>
      </nav>
    </div>
    <div class="card-body">
      <div v-if="!showSuccess" class="row">
        <div class="col-md-6 d-flex mb-10 align-items-center">
          <button :disabled="loading" class="btn btn-primary btn-sm" @click="goBack()">
            Back
          </button>
        </div>
        <div class="col-md-6 d-flex justify-content-end mb-10 align-items-center" style="gap:8px;">
          <button :disabled="loading || syncing" class="btn btn-primary btn-sm" @click="getInstanceDetails">
            <i :class="loading ? 'fa fa-spin fa-spinner' : 'fa fa-refresh'"></i>
            Refresh
          </button>
          <button :disabled="loading || syncing" class="btn btn-warning btn-sm" @click="syncInstance">
            <i :class="syncing ? 'fa fa-spin fa-spinner' : 'fa fa-cloud-upload'"></i>
            {{ syncing ? 'Syncing…' : 'Sync Instance' }}
          </button>
        </div>
        <div class="col-md-6">
          <div class="card inner-card action">
            <div class="card-header inner-card-header action-header">Actions <i class="fa fa-bolt"></i></div>
            <div class="card-body">
              <div class="row">
                <div class="col-md-3">
                  <button :disabled="loading || (details.status && details.status.toLowerCase() === 'running')" class="box-btn"
                          @click="actionHandler('start')">
                    <i v-if="action === 'start'" class="fa fa-spin fa-spinner text-success"></i>
                    <i v-else class="fa fa-play text-success"></i>
                    <span>Start Instance</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading || (details.status && details.status.toLowerCase() === 'stopped')"
                          class="box-btn" @click="actionHandler('stop')">
                    <i v-if="action === 'stop'" class="fa fa-spin fa-spinner text-danger"></i>
                    <i v-else class="fa fa-stop text-danger"></i>
                    <span>Stop Instance</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading || (details.status && details.status.toLowerCase() === 'stopped')" class="box-btn"
                          @click="actionHandler('restart')">
                    <i v-if="action === 'restart'" class="fa fa-spin fa-spinner text-primary"></i>
                    <i v-else class="fa fa-refresh text-primary"></i>
                    <span>Restart Instance</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading" class="box-btn" @click="actionHandler('adjustResource')">
                    <i class="fa fa-microchip text-warning"></i>
                    <span>Adjust Resources</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading" class="box-btn" @click="actionHandler('upgrade')">
                    <i class="fa fa-exchange text-info"></i>
                    <span>Change Profile</span>
                  </button>
                </div>
                <div v-if="user.userType === 'super-admin' || user.userType === 'admin'" class="col-md-3">
                  <button :disabled="isIncus || loading" class="box-btn" @click="actionHandler('delete')">
                    <i v-if="action === 'delete'" class="fa fa-spin fa-spinner text-danger"></i>
                    <i v-else class="fa fa-trash" style="color: red"></i>
                    <span>Delete Instance</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading" class="box-btn" @click="actionHandler('resize')">
                    <i class="fa fa-expand text-success"></i>
                    <span>Resize Disk</span>
                  </button>
                </div>
                <div class="col-md-3">
                  <button :disabled="loading" class="box-btn" @click="actionHandler('nodeGroup')">
                    <i class="fa fa-sitemap text-purple"></i>
                    <span>Node Group</span>
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card inner-card resource">
            <div class="card-header inner-card-header resource-header">Resource Usage</div>
            <div class="card-body p-0" style="height: 420px">
              <div v-if="loading" class="p-3"><div class="loader"></div></div>
              <div v-else class="res-grid">
                <!-- CPU -->
                <div class="res-tile">
                  <div class="res-tile__icon res-tile__icon--cpu"><i class="fa fa-microchip"></i></div>
                  <div class="res-tile__body">
                    <div class="res-tile__label">CPU</div>
                    <div class="res-tile__value">{{ (details.resources && details.resources.cpu) || 'N/A' }}</div>
                    <div class="res-tile__sub">cores</div>
                  </div>
                </div>
                <!-- Memory -->
                <div class="res-tile">
                  <div class="res-tile__icon res-tile__icon--ram"><i class="fa fa-server"></i></div>
                  <div class="res-tile__body">
                    <div class="res-tile__label">Memory</div>
                    <div class="res-tile__value">{{ (details.resources && details.resources.memory_usage) || 'N/A' }} <span class="res-tile__of">/ {{ (details.resources && details.resources.ram) || 'N/A' }}</span></div>
                    <div v-if="details.resources && details.resources.memory_usage_percent && details.resources.memory_usage_percent !== '-'" class="res-bar mt-1">
                      <div class="res-bar__fill res-bar__fill--ram" :style="progressBarStyle(details.resources.memory_usage_percent)"></div>
                    </div>
                    <div class="res-tile__sub">{{ (details.resources && details.resources.memory_usage_percent) || 'N/A' }}</div>
                  </div>
                </div>
                <!-- Disk -->
                <div class="res-tile">
                  <div class="res-tile__icon res-tile__icon--disk"><i class="fa fa-hdd-o"></i></div>
                  <div class="res-tile__body">
                    <div class="res-tile__label">Disk</div>
                    <div class="res-tile__value">{{ (details.resources && details.resources.disk_usage) || 'N/A' }} <span class="res-tile__of">/ {{ (details.resources && details.resources.disk) || 'N/A' }}</span></div>
                    <div v-if="details.resources && details.resources.disk_usage_percent && details.resources.disk_usage_percent !== '-'" class="res-bar mt-1">
                      <div class="res-bar__fill res-bar__fill--disk" :style="progressBarStyle(details.resources.disk_usage_percent)"></div>
                    </div>
                    <div class="res-tile__sub">{{ (details.resources && details.resources.disk_usage_percent) || 'N/A' }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card inner-card config">
            <div class="card-header inner-card-header font-config">Config</div>
            <div class="card-body p-0" style="height:670px">
              <div v-if="loading" class="p-3"><div class="loader"></div></div>
              <template v-else-if="details.config">
                <div class="cfg-search-wrap">
                  <i class="fa fa-search cfg-search-icon"></i>
                  <input v-model="configSearch" type="text" class="cfg-search" placeholder="Filter config keys…" />
                  <button v-if="configSearch" class="cfg-search-clear" @click="configSearch = ''"><i class="fa fa-times"></i></button>
                </div>
                <perfect-scrollbar class="scroll-area-config">
                  <div v-for="group in filteredConfigGroups" :key="group.name">
                    <div class="cfg-group-header">{{ group.name }}</div>
                    <div v-for="(item, i) in group.items" :key="i" class="cfg-row">
                      <span class="cfg-key" :title="item.key">{{ item.key }}</span>
                      <span class="cfg-val" :title="item.value">{{ item.value }}</span>
                      <button class="cfg-copy-btn" title="Copy value" @click="doCopy(item.value)"><i class="fa fa-copy"></i></button>
                    </div>
                  </div>
                  <div v-if="filteredConfigGroups.length === 0" class="cfg-empty">No matching keys.</div>
                </perfect-scrollbar>
              </template>
              <div v-else class="d-flex justify-content-center align-items-center" style="height: 670px">
                <span class="text-danger fw-600">No Config exists.</span>
              </div>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card inner-card information">
            <div class="card-header inner-card-header bg-info">Information</div>
            <div class="card-body p-0" style="height:670px">
              <div v-if="loading" class="p-3"><div class="loader"></div></div>
              <perfect-scrollbar v-else class="scroll-area-config">
                <div class="info-row">
                  <span class="info-label">Status</span>
                  <span class="info-val">
                    <span :class="details.status && details.status.toLowerCase() === 'running' ? 'badge-success' : 'badge-danger'" class="badge badge-pill">{{ details.status || '—' }}</span>
                  </span>
                </div>
                <div class="info-row">
                  <span class="info-label">Name</span>
                  <span class="info-val">{{ details.name || '—' }}</span>
                  <button v-if="details.name" class="info-copy-btn" @click="doCopy(details.name)"><i class="fa fa-copy"></i></button>
                </div>
                <div class="info-row">
                  <span class="info-label">IP Address</span>
                  <span class="info-val">{{ details.ip_address || '—' }}</span>
                  <button v-if="details.ip_address" class="info-copy-btn" @click="doCopy(details.ip_address)"><i class="fa fa-copy"></i></button>
                </div>
                <div class="info-row">
                  <span class="info-label">Node</span>
                  <span class="info-val">{{ details.node || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Node IP</span>
                  <span class="info-val">{{ details.node_ip || '—' }}</span>
                  <button v-if="details.node_ip" class="info-copy-btn" @click="doCopy(details.node_ip)"><i class="fa fa-copy"></i></button>
                </div>
                <div class="info-row">
                  <span class="info-label">Node Group</span>
                  <span class="info-val">{{ details.node_group || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Cluster</span>
                  <span class="info-val">{{ details.cluster || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Architecture</span>
                  <span class="info-val">{{ details.architecture || '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Profiles</span>
                  <span class="info-val">{{ details.profiles ? details.profiles.toString() : '—' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">Created</span>
                  <span class="info-val">{{ formatCreated(details.created) }}</span>
                </div>
              </perfect-scrollbar>
            </div>
          </div>
        </div>
        <div v-if="migration" class="col-md-12 mt-3">
          <div class="card inner-card migration">
            <div class="card-header inner-card-header migration-header">
              Migration <i class="fa fa-random"></i>
            </div>
            <div class="card-body">
              <div v-if="migrationLoading">
                <div class="loader"></div>
              </div>
              <div v-else>
                <div class="info-body">
                  <span>Migration ID</span>
                  <span class="fw-semibold">
                    {{ migration.migration_id || '-' }}
                    <button v-if="migration.migration_id" class="btn btn-link p-0 ml-1"
                            type="button" @click="doCopy(migration.migration_id)">
                      <i class="fa fa-copy"></i>
                    </button>
                  </span>
                </div>
                <div class="info-body">
                  <span>Status</span>
                  <p :class="migrationStatusBadgeClass(migration.status)"
                     class="badge badge-pill mb-0">
                    {{ formatMigrationStatus(migration.status) }}
                  </p>
                </div>
<!--                <div v-if="typeof migration.progress === 'number'" class="info-body">-->
<!--                  <span>Progress</span>-->
<!--                  <span style="width: 50%">-->
<!--                    <div class="d-flex justify-content-between align-items-center mb-1">-->
<!--                      <span class="fw-semibold">{{ migration.progress }}%</span>-->
<!--                    </div>-->
<!--                    <div class="progress" style="height: 14px;">-->
<!--                      <div-->
<!--                          :aria-valuenow="migration.progress"-->
<!--                          :style="{ width: migration.progress + '%' }"-->
<!--                          aria-valuemax="100"-->
<!--                          aria-valuemin="0"-->
<!--                          class="progress-bar bg-success progress-bar-striped"-->
<!--                          role="progressbar"-->
<!--                      ></div>-->
<!--                    </div>-->
<!--                  </span>-->
<!--                </div>-->
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div v-if="showSuccess" class="col-md-12">
          <svg class="checkmark" viewBox="0 0 52 52">
            <circle class="checkmark-circle" cx="26" cy="26" fill="none" r="25"/>
            <path class="checkmark-check" d="M14 27l7 7 16-16" fill="none"/>
          </svg>
          <h4 class="text-center fw-600">Instance Deleted Successfully</h4>
          <p class="text-center">
            Press a button below to either create a new instance or view the list of instances.
          </p>
          <div class="d-flex justify-content-center">
            <button class="btn btn-outline-info" @click="openInstanceList('create')">Create New Instance</button>
            <button class="ml-20 btn btn-danger btn-outline-danger"
                    @click="openInstanceList('list')">List Instances
            </button>
          </div>
        </div>
      </div>
    </div>
    <action-modal ref="actionModal" @refresh="getInstanceDetails"/>
  </div>
</template>

<script>
import actionModal from "@/views/admin/Lxd/details/actionModal.vue";
import {PerfectScrollbar} from 'vue2-perfect-scrollbar';
import swal from "sweetalert2";
import {db} from "@/firebase/init";
import copyButtonMixin from '../../../../mixins/copyButtonMixin';

export default {
  name: "index",
  mixins: [copyButtonMixin],
  components: {actionModal, PerfectScrollbar},
  mounted() {
    this.cluster = this.$route.params.cluster
    this.getInstanceDetails()
  },
  data() {
    return {
      cluster: '',
      details: {resources: {}, status: ''},
      loading: false,
      syncing: false,
      action: '',
      showSuccess: false,
      migration: null,
      migrationLoading: false,
      configSearch: '',
    }
  },
  computed: {
    filteredConfigGroups() {
      if (!this.details || !this.details.config) return []
      const q = this.configSearch.toLowerCase().trim()
      const prefixes = ['limits', 'security', 'user', 'volatile', 'image', 'raw', 'boot', 'environment', 'linux', 'migration', 'snapshots']
      const groups = {}
      Object.keys(this.details.config).forEach(key => {
        const val = String(this.details.config[key] != null ? this.details.config[key] : '')
        if (q && !key.toLowerCase().includes(q) && !val.toLowerCase().includes(q)) return
        const prefix = prefixes.find(p => key.startsWith(p + '.')) || 'other'
        if (!groups[prefix]) groups[prefix] = []
        groups[prefix].push({ key, value: val })
      })
      return Object.keys(groups).sort().map(name => ({ name, items: groups[name] }))
    },
    isIncus() {
      return ['incus-egv', 'incus-iad', 'dedi-egv', 'perf-egv', 'showit'].includes(this.$route.params.cluster)
    },
  },
  methods: {
    goBack() {
      this.$parent.$router.go(-1);
    },
    openInstanceList(type) {
      if(type === 'create') {
        this.$router.push({name: 'create-lxd'});
      } else if(type === 'list') {
        this.$router.push({name: 'lxd'});
      }
    },
    async actionHandler(action) {
      try {
        this.action = action;
        const instanceActionEndpoint = this.isIncus ? '/incus/instance/action' : '/lxd/instance/action'
        if (action === 'start') {
          const resp = await this.$http.post(instanceActionEndpoint, {
            grant_type: 'token',
            action,
            cluster: this.cluster,
            name: this.details.name
          })
          console.log('resp', resp.data)
        } else if (action === 'stop') {
          const resp = await this.$http.post(instanceActionEndpoint, {
            grant_type: 'token',
            action,
            cluster: this.cluster,
            name: this.details.name
          })
        } else if (action === 'restart') {
          const resp = await this.$http.post(instanceActionEndpoint, {
            grant_type: 'token',
            action,
            cluster: this.cluster,
            name: this.details.name
          })
          console.log('resp', resp.data)
        } else if (action === 'delete') {
          swal({
            title: "Are you sure?",
            text: "Are you sure that you want to delete the instance?",
            type: "question",
            confirmButtonText: "Yes Proceed",
            confirmButtonColor: "#6ACF98",
            showCancelButton: true,
            cancelButtonText: "No Thanks",
            cancelButtonColor: "#EE8057"
          }).then(async result => {
            if (result.value) {
              this.action = action
              const resp = await this.$http.post('/lxd/instance/action', {
                grant_type: 'token',
                action,
                cluster: this.cluster,
                name: this.details.name
              })
              console.log('resp', resp.data)
              this.showSuccess = resp.data.success
            }
          })
          return
        } else if (action === 'adjustResource') {
          this.$refs.actionModal.load({
            title: 'Adjust Resource',
            action: 'adjustResource',
            ram: this.details.resources && this.details.resources.ram ? parseFloat(this.details.resources.ram) : 0,
            cpu: this.details.resources && this.details.resources.cpu ? (this.details.resources.cpu) : 0,
            ramUnit: this.details.resources && this.details.resources.ram ? this.details.resources.ram.replace(parseFloat(this.details.resources.ram), '') : 'GB',
            cluster: this.cluster,
            name: this.details.name,
            isIncus: this.isIncus,
          });
          return

        } else if (action === 'upgrade') {
          this.$refs.actionModal.load({
            title: 'Change Profile',
            action: 'upgrade',
            cluster: this.cluster,
            name: this.details.name,
            profile: this.details.profiles && this.details.profiles.length ? this.details.profiles[0] : 'wpo-starter',
            isIncus: this.isIncus,
          });
          return
        } else if (action === 'resize') {
          this.$refs.actionModal.load({
            title: 'Resize Disk',
            action: 'resize',
            cluster: this.cluster,
            name: this.details.name,
            disk: this.details.resources && this.details.resources.disk ? parseFloat(this.details.resources.disk) : 0,
            diskUnit: this.details.resources && this.details.resources.disk ? this.details.resources.disk.replace(parseFloat(this.details.resources.disk), '') : 'GiB',
            useNewApi: this.isIncus,
          });
          return
        } else if (action === 'nodeGroup') {
          this.$refs.actionModal.load({
            title: 'Node Group',
            action: 'nodeGroup',
            cluster: this.cluster,
            name: this.details.name,
            nodeGroup: this.details.node_group || '',
            details: this.details,
          });
          return
        }
        this.getInstanceDetails()
        this.action = ''
      } catch (e) {
        console.log('error occurred', e);
        this.showToast({
          message: `Something went wrong! ${e.toString()}`,
          duration: 2000
        }, true, 'danger')
        this.action = ''
      }
    },
    formatCreated(date) {
      if (!date) return '—'
      return moment(date).format('D MMM YYYY, HH:mm')
    },
    progressBarStyle(percentage) {
      console.log('progress', percentage);
      if (!percentage || percentage === '-') return
      // Determine color gradient based on percent
      const percent = Number(percentage.split('%')[0]);
      let gradient;
      const gradientColors = [
        '#2ecc40', // Green
        '#a3e635', // Lime
        '#eab308', // Yellow
        '#f97316', // Orange
        '#ef4444', // Red
        '#b91c1c', // Deep Red
        '#7f1d1d'  // Very Deep Red
      ];
      console.log(gradientColors.slice(0, 2).join(','))
      if (percent <= 20) {
        gradient = `linear-gradient(to right, ${gradientColors[0]}, #27ae60)`;
      } else if (percent <= 40) {
        gradient = `linear-gradient(to right, ${gradientColors.slice(0, 2).join(',')})`;
      } else if (percent <= 60) {
        gradient = `linear-gradient(to right, ${gradientColors.slice(0, 3).join(',')})`;
      } else if (percent <= 75) {
        gradient = `linear-gradient(to right, ${gradientColors.slice(0, 4).join(',')})`;
      } else if (percent <= 90) {
        gradient = `linear-gradient(to right, ${gradientColors.slice(0, 5).join(',')}`;
      } else if (percent <= 95) {
        gradient = `linear-gradient(to right, ${gradientColors.slice(0, 6).join(',')})`;
      } else {
        gradient = 'linear-gradient(to right, #a3e635, red)';
      }

      return {
        width: percent + '%',
        background: gradient,
        transition: 'width 0.5s ease, background 0.5s ease'
      };
    },
    convertToMB(value) {
      console.log(value)
      if (!value) return 0;
      const num = parseFloat(value); // extract numeric part
      if (value.toLowerCase().includes("gb") || value.toLowerCase().includes("gib")) {
        return num * 1024; // 1 GB = 1024 MB
      } else if (value.toLowerCase().includes("mb")) {
        return num;
      } else {
        throw new Error("Unknown unit, must be MB or GB");
      }
    },
    async syncInstance() {
      try {
        this.syncing = true
        const resp = await this.$http.post('/lxd/instance/action', {
          grant_type: 'token',
          action: 'sync',
          cluster: this.cluster,
          name: this.details.name
        })
        if (resp.data && resp.data.success) {
          this.$notify({ group: 'foo', title: 'Success', text: 'Instance synced successfully.' })
          await this.getInstanceDetails()
        } else {
          this.$notify({ group: 'foo', title: 'Error', text: resp.data.message || 'Sync failed.' })
        }
      } catch (e) {
        console.error('syncInstance', e)
        this.$notify({ group: 'foo', title: 'Error', text: 'Sync failed. Please try again.' })
      } finally {
        this.syncing = false
      }
    },
    async getInstanceDetails() {
      try {
        this.loading = true;
        const name = this.$route.params.id;
        const cluster = this.$route.params.cluster
        const isIncus = this.isIncus
        const endpoint = isIncus ? '/incus/get-details' : '/lxd/get-details'
        const resp = await this.$http.post(endpoint, {
          name,
          cluster,
          grant_type: 'token'
        })
        console.log('response', resp.data)
        const raw = resp.data.data
        if (isIncus) {
          this.details = {
            ...raw,
            ip_address: raw.ip_address || raw.instance_ip || null,
            node: raw.node || raw.location || null,
            created: raw.created || raw.created_at || null,
            cluster: raw.cluster || cluster,
            node_group: raw.node_group || (raw.expanded_config && raw.expanded_config['user.node_group']) || null,
            node_ip: raw.node_ip || null,
            resources: raw.resources || (raw.expanded_config ? {
              cpu: raw.expanded_config['limits.cpu'] || null,
              ram: raw.expanded_config['limits.memory'] || null,
              disk: (raw.expanded_devices && raw.expanded_devices.root && raw.expanded_devices.root.size) || null,
              disk_usage: null,
              disk_usage_percent: '-',
              memory_usage: null,
              memory_usage_percent: '-',
            } : null),
          }
        } else {
          this.details = raw
        }
        this.loading = false
        if (this.details.resources) {
          let {disk, disk_usage, disk_usage_percent} = this.details.resources
          console.log('disk_usage', disk_usage_percent)
          if (disk && disk_usage && disk_usage_percent === '-') {
            const usageMB = this.convertToMB(disk_usage);
            const totalMB = this.convertToMB(disk);

            if (totalMB !== 0) {
              this.details.resources.disk_usage_percent = `${((usageMB / totalMB) * 100).toFixed(2)}%`;
            }
          }
          let {memory_usage, ram, memory_usage_percent} = this.details.resources;
          if (memory_usage && ram && memory_usage_percent === '-') {
            const usageMB = this.convertToMB(memory_usage);
            const totalMB = this.convertToMB(ram);

            if (totalMB !== 0) {
              this.details.resources.memory_usage_percent = `${((usageMB / totalMB) * 100).toFixed(2)}%`;
            }
          }
          console.log('memory_usage_percent', memory_usage_percent)
        }
        // Fetch any migration log tied to this instance
        await this.fetchMigration(this.details && this.details.name);
        console.log('called migration')
      } catch (e) {
        console.log('error', e)
        this.loading = false
      }
    },
    async fetchMigration(instanceName) {
      console.log('calling fetchMigration');
      if (!instanceName) {
        this.migration = null;
        return;
      }
      try {
        this.migrationLoading = true;
        const snap = await db.collection('nodeGroupLogs')
            .where('instance', '==', instanceName)
            .limit(1)
            .get();
        console.log('snap.size', snap.size)
        if (snap.empty) {
          this.migration = null;
        } else {
          const data = snap.docs[0].data() || {};
          this.migration = {
            migration_id: data.migration_id || '',
            instance: data.instance || instanceName,
            cluster: data.cluster || '',
            status: data.status || '',
            step: data.step || 0,
            total_steps: data.total_steps || 0,
            step_name: data.step_name || '',
            progress: typeof data.progress === 'number' ? data.progress : null,
            source_node: data.source_node || '',
            target_node: data.target_node || '',
            error: data.error || '',
            started_at: data.started_at && data.started_at.toDate
                ? data.started_at.toDate()
                : data.started_at || null
          };
        }
      } catch (e) {
        console.log('error fetching migration log', e);
        this.migration = null;
      } finally {
        this.migrationLoading = false;
      }
    },
    formatMigrationStatus(status) {
      if (!status) return '-';
      return status
          .toString()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, c => c.toUpperCase());
    },
    migrationStatusBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'completed' || s === 'success' || s === 'done') return 'badge-success';
      if (s === 'failed' || s === 'error') return 'badge-danger';
      return 'badge-warning';
    }
  }
}
</script>

<style scoped>
.inner-card {
  border: 1px solid #ccc;
}

.inner-card-header {
  //background: lightgrey !important;
  border-top-left-radius: 9px;
  border-top-right-radius: 9px;
  padding: 10px;
}

.information {
  border-left: 5px solid #ff0000;
}

.info-body {
  display: flex;
  justify-content: space-between;
  font-size: 16px;
  margin-bottom: 10px;
}

.config {
  border-left: 5px solid green;
}

.font-config {
  color: green;
  font-weight: bold;
  background: #20940669 !important;
}

.info-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 8px;
  border: 1px solid #e5e7eb; /* light gray border */
  border-radius: 6px;
  background-color: #f9fafb; /* subtle background */
}

.info-body span:first-child {
  font-weight: 600;
  color: #6b7280; /* gray-600 */
}

.info-body span:last-child {
  font-weight: 600;
  color: #3b82f6; /* light blue */
}

.bg-info {
  background: #da5d5d45 !important;
  color: #ff0000;
  font-weight: bold;
}

.resource-header {
  background: #C7E4EA !important;
  color: cadetblue;
  font-weight: bold;
}

.resource {
  border-left: 5px solid cadetblue;
}

/* Information rows */
.info-row {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  border-bottom: 1px solid #f1f3f5;
  gap: 8px;
  font-size: 13px;
}
.info-row:hover { background: #f8f9fa; }
.info-label {
  flex: 0 0 38%;
  font-weight: 600;
  color: #6c757d;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.info-val {
  flex: 1;
  color: #3b5bdb;
  font-family: 'SFMono-Regular', Consolas, monospace;
  font-size: 12.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.info-copy-btn {
  flex: 0 0 auto;
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 11px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}
.info-row:hover .info-copy-btn { opacity: 1; }
.info-copy-btn:hover { color: #495057; background: #e9ecef; }

/* Resource tiles */
.res-grid {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  height: 100%;
}
.res-tile {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 10px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
}
.res-tile__icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
}
.res-tile__icon--cpu { background: #e7f5ff; color: #1971c2; }
.res-tile__icon--ram { background: #f3f0ff; color: #6741d9; }
.res-tile__icon--disk { background: #ebfbee; color: #2f9e44; }
.res-tile__body { flex: 1; min-width: 0; }
.res-tile__label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #868e96;
  margin-bottom: 2px;
}
.res-tile__value {
  font-size: 18px;
  font-weight: 700;
  color: #212529;
  font-family: 'SFMono-Regular', Consolas, monospace;
}
.res-tile__of {
  font-size: 13px;
  font-weight: 400;
  color: #868e96;
}
.res-tile__sub {
  font-size: 11px;
  color: #adb5bd;
  margin-top: 2px;
}
.res-bar {
  height: 6px;
  background: #dee2e6;
  border-radius: 99px;
  overflow: hidden;
}
.res-bar__fill {
  height: 100%;
  border-radius: 99px;
  transition: width 0.4s ease;
}
.res-bar__fill--ram { background: #7950f2; }
.res-bar__fill--disk { background: #2f9e44; }

.action-header {
  background: #95520a2b !important;
  color: #95520a;
  font-weight: bold;
}

.action {
  border-left: 5px solid #95520a;
}

.migration-header {
  background: #e7f0ff !important;
  color: #3b82f6;
  font-weight: bold;
}

.migration {
  border-left: 5px solid #3b82f6;
}

.box-btn {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%; /* fixed width */
  height: 180px; /* fixed height */
  padding: 20px;
  background: white;
  color: black;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 600;
  gap: 12px; /* space between icon and text */
  transition: all 0.25s ease;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
  text-align: center;
  margin-bottom: 20px;
}

.box-btn:disabled {
  background: #80808042 !important;
}

.box-btn i {
  font-size: 28px;
}

.box-btn:hover {
  background: #f9fafb;
  transform: translateY(-3px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1);
}

.box-btn:active {
  transform: scale(0.97);
}

.cfg-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  padding: 8px 12px;
  border-bottom: 1px solid #e9ecef;
}
.cfg-search-icon {
  position: absolute;
  left: 20px;
  color: #adb5bd;
  font-size: 12px;
}
.cfg-search {
  width: 100%;
  padding: 5px 28px 5px 28px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 12px;
  outline: none;
}
.cfg-search:focus { border-color: #80bdff; }
.cfg-search-clear {
  position: absolute;
  right: 20px;
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  font-size: 12px;
  padding: 0;
}
.cfg-group-header {
  padding: 4px 12px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6c757d;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  border-top: 1px solid #e9ecef;
  margin-top: 2px;
}
.cfg-row {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border-bottom: 1px solid #f1f3f5;
  gap: 8px;
  font-size: 12px;
}
.cfg-row:hover { background: #f8f9fa; }
.cfg-key {
  flex: 0 0 45%;
  font-family: 'SFMono-Regular', Consolas, monospace;
  color: #495057;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cfg-val {
  flex: 1;
  color: #3b5bdb;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: 'SFMono-Regular', Consolas, monospace;
}
.cfg-copy-btn {
  flex: 0 0 auto;
  background: none;
  border: none;
  color: #adb5bd;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 11px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.15s;
}
.cfg-row:hover .cfg-copy-btn { opacity: 1; }
.cfg-copy-btn:hover { color: #495057; background: #e9ecef; }
.cfg-empty {
  padding: 24px;
  text-align: center;
  color: #adb5bd;
  font-size: 13px;
}
.scroll-area-config {
  position: relative;
  max-height: 610px !important;
  overflow: hidden;
}
.checkmark {
  width: 100px;
  height: 100px;
  margin: 0 auto;
  stroke-width: 3;
  stroke: #22c55e;
  stroke-miterlimit: 10;
  border-radius: 50%;
  display: block;
}

.checkmark-circle {
  stroke-dasharray: 157;
  stroke-dashoffset: 157;
  stroke: #22c55e;
  animation: circleDraw 0.7s ease-in-out forwards;
}

.checkmark-check {
  stroke-dasharray: 48;
  stroke-dashoffset: 48;
  stroke: #22c55e;
  animation: checkDraw 0.5s ease-in-out forwards;
  animation-delay: 0.7s;
}

@keyframes circleDraw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes checkDraw {
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes pop {
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  60% {
    transform: scale(1.05);
    opacity: 1;
  }
  100% {
    transform: scale(1);
  }
}
</style>
